import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth-store'
import { useUserStore } from '../stores/user-store'
import { useToast } from 'vuestic-ui'

/**
 * Composable para manejar operaciones de autenticación
 * Separa la lógica de negocio de los componentes UI
 */
export const useAuth = () => {
  const router = useRouter()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const { init: notify } = useToast()

  /**
   * Cierra sesión del usuario
   * - Llama al API de logout
   * - Limpia el estado local
   * - Redirige al login
   */
  const logout = async (): Promise<void> => {
    try {
      await authStore.logout()
      userStore.clearUser()
      notify({
        message: 'Sesión cerrada correctamente',
        color: 'success',
      })
      await router.push({ name: 'login' })
    } catch (error) {
      notify({
        message: 'Error al cerrar sesión',
        color: 'danger',
      })
      // Aún así redirige al login por seguridad
      await router.push({ name: 'login' })
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = (): boolean => {
    return authStore.isLoggedIn
  }

  /**
   * Obtiene el usuario actual
   */
  const getCurrentUser = () => {
    return authStore.currentUser
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  const hasPermission = (permission: string): boolean => {
    return authStore.hasPermission(permission)
  }

  /**
   * Verifica si el usuario tiene alguno de los permisos especificados
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return authStore.hasAnyPermission(permissions)
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return authStore.hasAllPermissions(permissions)
  }

  return {
    logout,
    isAuthenticated,
    getCurrentUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }
}
